// Shared rental-duration helper used across all pages so the search summary,
// vehicle cards, booking review and helmet math never diverge.
//
// Rule (matches Zoho business logic):
//   • Compute completed 24-hour days.
//   • Look at the remainder (minutes).
//   • "Raw" extra-hour count = ceil(remainder_minutes / 60). If this is > 6
//     the rental rounds UP to the next full day — display "N DAYS", no
//     extra-hour pricing. (This matches Zoho's "6+ hours → +1 day" rule.)
//   • Otherwise apply the 30-minute GRACE on the sub-hour minutes:
//       floor_hours = floor(remainder_minutes / 60)
//       leftover    = remainder_minutes - floor_hours × 60
//       hoursPart   = (leftover > 30) ? floor_hours + 1 : floor_hours
//     Pricing uses DAY × N + HOUR × hoursPart.
export const computeDurationDetail = (startDtStr, endDtStr) => {
  const empty = { days: 0, extraHours: 0, display: "0 HOURS" };
  if (!startDtStr || !endDtStr) return empty;
  const s = startDtStr instanceof Date ? startDtStr : new Date(startDtStr);
  const e = endDtStr instanceof Date ? endDtStr : new Date(endDtStr);
  if (isNaN(s) || isNaN(e) || e <= s) return empty;

  const totalMinutes = Math.round((e - s) / 60000);
  const fullDays = Math.floor(totalMinutes / (24 * 60));
  const remainingMinutes = totalMinutes - fullDays * 24 * 60;

  // Builds the human-readable "N DAY[S] M HOUR[S]" string without ever
  // forcing a 1-day minimum. Hours-only durations display as "M HOURS".
  const fmt = (d, h) => {
    if (d === 0 && h === 0) return "0 HOURS";
    const dayStr = d > 0 ? `${d} ${d === 1 ? "DAY" : "DAYS"}` : "";
    const hourStr = h > 0 ? `${h} ${h === 1 ? "HOUR" : "HOURS"}` : "";
    return [dayStr, hourStr].filter(Boolean).join(" ");
  };

  // Exactly N full days — no remainder.
  if (remainingMinutes === 0) {
    return { days: fullDays, extraHours: 0, display: fmt(fullDays, 0) };
  }

  // Raw ceil-hours decides the >6 cutoff (6h1m → ceil=7 → +1 day).
  // This rule only kicks in when fullDays ≥ 1 so we never roll an "N hours"
  // short rental up to "1 day".
  const rawCeilHours = Math.ceil(remainingMinutes / 60);
  if (fullDays >= 1 && rawCeilHours > 6) {
    const d = fullDays + 1;
    return { days: d, extraHours: 0, display: fmt(d, 0) };
  }

  // ≤ 6 extra hours — apply the 30-min grace on the sub-hour minutes.
  const floorHours = Math.floor(remainingMinutes / 60);
  const leftover = remainingMinutes - floorHours * 60;
  const hoursPart = leftover > 30 ? floorHours + 1 : floorHours;

  if (hoursPart === 0) {
    return { days: fullDays, extraHours: 0, display: fmt(fullDays, 0) };
  }
  return {
    days: fullDays,
    extraHours: hoursPart,
    display: fmt(fullDays, hoursPart),
  };
};

// "YYYY-MM-DD" + "HH:MM" → Date (local)
export const buildLocalDate = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  return new Date(y, m - 1, d, hh, mm);
};

const _fmt = (dt) => {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  const hh = String(dt.getHours()).padStart(2, "0");
  const mm = String(dt.getMinutes()).padStart(2, "0");
  return { date: `${y}-${m}-${d}`, time: `${hh}:${mm}` };
};

// Raw rental duration in hours (no 30-min grace, no 6-hour rounding).
// Used by the 24-hour minimum enforcement.
export const getRentalHours = (startDate, startTime, endDate, endTime) => {
  const s = buildLocalDate(startDate, startTime);
  const e = buildLocalDate(endDate, endTime);
  if (!s || !e) return 0;
  const diffMs = e.getTime() - s.getTime();
  if (diffMs <= 0) return 0;
  return diffMs / (1000 * 60 * 60);
};

// Add `days` whole 24-hour periods to a (YYYY-MM-DD, HH:MM) pair.
export const addDaysToDateTime = (dateStr, timeStr, days) => {
  if (!dateStr || !timeStr) return { date: dateStr, time: timeStr };
  const dt = buildLocalDate(dateStr, timeStr);
  if (!dt) return { date: dateStr, time: timeStr };
  dt.setDate(dt.getDate() + days);
  return _fmt(dt);
};

// Add `hours` whole hours to a (YYYY-MM-DD, HH:MM) pair.
export const addHoursToDateTime = (dateStr, timeStr, hours) => {
  if (!dateStr || !timeStr) return { date: dateStr, time: timeStr };
  const dt = buildLocalDate(dateStr, timeStr);
  if (!dt) return { date: dateStr, time: timeStr };
  dt.setHours(dt.getHours() + hours);
  return _fmt(dt);
};
